import * as React from "react";
import {
    Box,
    IconButton,
    Typography,
    Drawer,
    Alert,
    Button,
    Divider,
    Grid,
    Snackbar,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Tooltip
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import close from "../../assets/icons/close.png";
import pdfFileImg from "../../assets/images/pdfIcon.png";

import { getKamProfilePicApi } from "../Services/OrgRepo";
import {
    DeleteDrawingApi,
    DeleteDrawingPayload,
    uploadDrawingApi
} from "../Services/FeasibleRFQ";
import {
    accountManager,
    accountManagerHead,
    generatPartDrawingUrl,
    generatProfilePicUrl
} from "../../Utils/Constant";
import { showToast } from "../../Components/Toast";

interface DrawingDrawerProps {
    open: boolean;
    onClose: () => void;
    selectedRowData: any;
    partID: string;
    onRefresh?: () => void;
    from?: string;
}

interface DrawingItem {
    id: number;
    drawing: string; // "Drawing 1", "Drawing 2"
    filePath?: string;
    file?: File;
}

export const DrawingDrawer: React.FC<DrawingDrawerProps> = ({
    open,
    onClose,
    selectedRowData,
    partID,
    onRefresh,
    from
}) => {
    const [profileImage, setProfileImage] = React.useState("");
    const [drawings, setDrawings] = React.useState<DrawingItem[]>([]);
    const [selectedRows, setSelectedRows] = React.useState<number[]>([]);
    const [uploading, setUploading] = React.useState(false);
    const [error, setError] = React.useState("");
    const [confirmDelete, setConfirmDelete] = React.useState(false);

    // 🔹 Fetch KAM profile picture
    const getProfilePic = async () => {
        try {
            const data = await getKamProfilePicApi(selectedRowData?.KAMId);
            if (data?.status) {
                const img = generatProfilePicUrl(
                    selectedRowData?.KAMId,
                    data?.data,
                    "Profile_Picture"
                );
                setProfileImage(img);
            } else setProfileImage("");
        } catch {
            setProfileImage("");
        }
    };

    // 🔹 Prepare drawings list
    const refreshDrawings = React.useCallback(() => {
        const mainDrawings =
            typeof selectedRowData?.drawings === "string" &&
            selectedRowData?.drawings
                ? [selectedRowData.drawings]
                : Array.isArray(selectedRowData?.drawings)
                  ? selectedRowData.drawings
                  : [];

        const additional = Array.isArray(
            selectedRowData?.additionalDrawingsPath
        )
            ? selectedRowData.additionalDrawingsPath.filter(
                  (p) => p && p.trim() !== ""
              )
            : [];

        const combined = [...mainDrawings, ...additional].map(
            (path, index) => ({
                id: index + 1,
                drawing: `Drawing ${index + 1}`,
                filePath: path.trim()
            })
        );

        setDrawings(combined);
    }, [selectedRowData]);

    // 🔹 Rebuild drawings when parent updates
    React.useEffect(() => {
        if (selectedRowData?.KAMId) getProfilePic();
        refreshDrawings();
        setSelectedRows([]);
    }, [
        selectedRowData?.drawings,
        selectedRowData?.additionalDrawingsPath,
        selectedRowData?.KAMId,
        refreshDrawings
    ]);

    // 🔹 Handle file upload
    const handleUploadFiles = async (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length) return;

        const invalid = files.find(
            (f) => f.type !== "application/pdf" || f.size > 50 * 1024 * 1024
        );
        if (invalid) {
            setError("Only PDF files under 50MB are allowed.");
            return;
        }

        try {
            setUploading(true);
            await uploadDrawingApi(partID, files);

            showToast({ type: "success", message: "Drawing uploaded" });
            onClose();
            if (onRefresh) await onRefresh();

            setTimeout(onClose, 500);
        } catch {
            showToast({ type: "error", message: "Failed to upload file" });
        } finally {
            setUploading(false);
        }
    };

    // 🔹 Handle download
    const handleDownloadDrawing = () => {
        if (!selectedRows.length) return;

        selectedRows.forEach((id) => {
            const row = drawings.find((d) => d.id === id);
            if (!row) return;

            if (row.filePath) {
                const url = generatPartDrawingUrl(
                    selectedRowData?.partID,
                    row.filePath
                );
                // ✅ Open each PDF in a new tab
                window.open(url, "_blank", "noopener");
            } else if (row.file) {
                // ✅ For locally uploaded file
                const blobUrl = URL.createObjectURL(row.file);
                window.open(blobUrl, "_blank", "noopener,noreferrer");
            }
        });
    };

    // 🔹 Handle delete (local only)
    // const handleDeleteConfirm = () => {
    //   const updated = drawings.filter((row) => !selectedRows.includes(row.id));
    //   setDrawings(updated);
    //   setSelectedRows([]);
    //   setConfirmDelete(false);
    // };

    const handleDeleteConfirm = async () => {
        try {
            // You can loop or batch based on your backend’s needs
            for (const id of selectedRows) {
                const drawing = drawings.find((d) => d.id === id);
                const filePath =
                    selectedRowData.additionalDrawingsPath?.[0] || "";
                const fileName = filePath.split("/").pop();

                if (!drawing) continue;
                const payload: DeleteDrawingPayload = {
                    CORFQ_Part_ID: selectedRowData.partID, // or fixed value
                    File_Name: fileName
                };

                const res = await DeleteDrawingApi(payload);
                showToast({ type: "error", message: "Drawing(s) deleted" });
                onClose();
                if (onRefresh) await onRefresh();

                console.log("Delete API response:", res);
            }

            // Update UI after successful delete(s)
            const updated = drawings.filter(
                (row) => !selectedRows.includes(row.id)
            );
            setDrawings(updated);
            setSelectedRows([]);
            setConfirmDelete(false);
        } catch (err) {
            console.error("Delete failed:", err);
        }
    };

    const columns: GridColDef[] = [
        {
            field: "drawing",
            headerName: "Drawings",
            flex: 1,
            disableColumnMenu: true,
            renderCell: (params) => (
                <Box display="flex" alignItems="center" gap={1}>
                    <img src={pdfFileImg} width="20px" alt="file" />
                    <Typography variant="body2">
                        {params.row.drawing}
                    </Typography>
                </Box>
            )
        }
    ];

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box sx={{ p: 2, width: { xs: "100%", sm: 400 } }}>
                {/* Header */}
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography variant="h6" fontWeight="bold">
                        Drawings
                    </Typography>
                    <IconButton onClick={onClose}>
                        <img src={close} width={14} alt="Close" />
                    </IconButton>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* RFQ Info */}
                <Grid container spacing={2}>
                    <Grid item xs={5}>
                        <Typography color="text.secondary">
                            Company RFQ ID
                        </Typography>
                    </Grid>
                    <Grid item xs={7}>
                        <Typography>{selectedRowData?.rfqId}</Typography>
                    </Grid>
                    <Grid item xs={5}>
                        <Typography color="text.secondary">
                            Created By
                        </Typography>
                    </Grid>
                    <Grid item xs={7}>
                        <Typography
                            sx={{ display: "flex", alignItems: "center" }}
                        >
                            {profileImage && (
                                <img
                                    src={profileImage}
                                    width={24}
                                    height={24}
                                    style={{
                                        borderRadius: "50%",
                                        marginRight: 5
                                    }}
                                    alt="KAM"
                                />
                            )}
                            {selectedRowData?.keyAccountManager || "-"}
                        </Typography>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Drawing Controls */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 2
                    }}
                >
                    <Typography fontWeight="bold">Drawing List</Typography>
                    <Box display="flex" gap={1}>
                        {from === "CompanyRfq" && accountManager() && (
                            <>
                                <Tooltip title="Upload">
                                    <LoadingButton
                                        variant="outlined"
                                        loading={uploading}
                                        disabled={drawings.length >= 3}
                                        component="label"
                                    >
                                        {!uploading && (
                                            <FileUploadOutlinedIcon />
                                        )}
                                        <input
                                            type="file"
                                            hidden
                                            accept="application/pdf"
                                            multiple
                                            onChange={handleUploadFiles}
                                        />
                                    </LoadingButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                    <Button
                                        color="error"
                                        variant="outlined"
                                        disabled={
                                            selectedRows.length === 0 ||
                                            selectedRows.length ===
                                                drawings.length
                                        }
                                        onClick={() => setConfirmDelete(true)}
                                    >
                                        <DeleteOutlineOutlinedIcon />
                                    </Button>
                                </Tooltip>
                            </>
                        )}
                        <Tooltip title="Download">
                            <Button
                                color="secondary"
                                variant="outlined"
                                disabled={!selectedRows.length}
                                onClick={handleDownloadDrawing}
                            >
                                <FileDownloadOutlinedIcon />
                            </Button>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Drawing List */}
                <DataGrid
                    rows={drawings}
                    columns={columns}
                    checkboxSelection
                    onRowSelectionModelChange={(ids) =>
                        setSelectedRows(ids as number[])
                    }
                    hideFooter
                    autoHeight
                />

                {/* Error Snackbar */}
                <Snackbar
                    open={!!error}
                    autoHideDuration={4000}
                    onClose={() => setError("")}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert
                        onClose={() => setError("")}
                        severity="error"
                        variant="filled"
                    >
                        {error}
                    </Alert>
                </Snackbar>

                {/* Delete Dialog */}
                <Dialog
                    open={confirmDelete}
                    onClose={() => setConfirmDelete(false)}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>Delete Drawings</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to delete the selected
                            drawing(s)?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setConfirmDelete(false)}
                            size="small"
                            color="error"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDeleteConfirm}
                            size="small"
                            color="primary"
                        >
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Drawer>
    );
};
